import type { ComponentProps, FormHTMLAttributes, InputHTMLAttributes } from 'react'
import type { AdminRouterContext } from '@/lib/router-context'
import type {
  CondolencesIntegration,
  CondolencesOrdersRefresh,
} from '@/lib/services/condolences/condolences.types'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import CondolencesIcon from '@/assets/CondolencesIcon'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  condolencesQueries,
  useCheckCondolencesTokenMutation,
  useConnectCondolencesLoginMutation,
  useRefreshCondolencesOrdersMutation,
  useSuspenseCondolencesIntegration,
  useUpdateCondolencesIntegrationMutation,
} from '@/lib/services/condolences/condolences.query'
import { connectCondolencesLoginSchema } from '@/lib/services/condolences/condolences.schema'
import { getErrorMessage, readProperty } from '@/lib/utils'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { AlertTriangle, KeyRound, RefreshCw, ShieldCheck, Truck, Unplug } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

// Preserve the page's existing empty integration default without weakening the API resource.
type CondolencesSettingsIntegration = Partial<
  Pick<
    CondolencesIntegration,
    'connected' | 'has_token' | 'last_error' | 'vendor_name' | 'vendor_id' | 'token_preview'
  >
>

const autofillOffFormProps = {
  autoComplete: 'off',
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-protonpass-ignore': 'true',
} satisfies FormHTMLAttributes<HTMLFormElement> & Record<`data-${string}`, string>

const autofillOffInputProps = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'none',
  spellCheck: false,
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-protonpass-ignore': 'true',
  'data-form-type': 'other',
} satisfies InputHTMLAttributes<HTMLInputElement> & Record<`data-${string}`, string>

const condolencesCredentialInputBaseProps = {
  ...autofillOffInputProps,
  autoComplete: 'one-time-code',
}

export async function condolencesSettingsLoader({
  context,
  params,
}: {
  context: {
    ability: Pick<AdminRouterContext['ability'], 'can'>
    queryClient: AdminRouterContext['queryClient']
  }
  params: { orgId: string | number }
}): Promise<{ isReadOnly: boolean }> {
  if (
    !context.ability.can('list', 'business-settings') ||
    !context.ability.can('list', 'condolences')
  ) {
    throw notFound()
  }

  await context.queryClient.ensureQueryData(
    condolencesQueries.integration({ organizationId: params.orgId })
  )

  return { isReadOnly: !context.ability.can('edit', 'condolences') }
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/integrations/condolences/')({
  component: CondolencesSettingsPage,
  loader: condolencesSettingsLoader,
})

function validationErrorsForForm(error: unknown, knownFields: string[] = []) {
  const errors = readProperty(readProperty(readProperty(error, 'response'), 'data'), 'errors')
  if (!errors) return undefined

  const entries: [string, unknown][] = Object.entries(errors)
  return entries.reduce<{ form: unknown[]; fields: Record<string, unknown[]> }>(
    (result, [key, messages]) => {
      const normalizedMessages: unknown[] = Array.isArray(messages) ? messages : [messages]

      if (knownFields.includes(key)) {
        return {
          ...result,
          fields: {
            ...result.fields,
            [key]: normalizedMessages,
          },
        }
      }

      return {
        ...result,
        form: [...result.form, ...normalizedMessages],
      }
    },
    { form: [], fields: {} }
  )
}

function tokenStatusBadge(
  integration: Partial<Pick<CondolencesIntegration, 'connected' | 'has_token'>> = {}
): { label: string; variant: ComponentProps<typeof Badge>['variant'] } {
  if (integration.connected) {
    return { label: 'Connected', variant: 'success' }
  }

  if (integration.has_token) {
    return { label: 'Needs attention', variant: 'destructive' }
  }

  return { label: 'Not connected', variant: 'outline' }
}

function refreshOrdersDescription(response: Partial<CondolencesOrdersRefresh> | null = {}): string {
  const seen = response?.seen ?? 0
  const imported = response?.imported ?? 0
  const skipped = response?.skipped ?? 0
  const failed = response?.failed ?? 0
  const parts = [`${seen} checked`, `${imported} imported`]

  if (skipped > 0) {
    parts.push(`${skipped} skipped`)
  }

  if (failed > 0) {
    parts.push(`${failed} failed`)
  }

  return `${parts.join(', ')}.`
}

export function CondolencesSettingsPage() {
  const orgId = useRouteOrganizationIdParam()
  const { isReadOnly } = useIsReadOnly()
  const { data: integration = {} }: { data?: CondolencesSettingsIntegration } =
    useSuspenseCondolencesIntegration({
      organizationId: orgId,
    })
  const updateMutation = useUpdateCondolencesIntegrationMutation()
  const connectLoginMutation = useConnectCondolencesLoginMutation()
  const checkTokenMutation = useCheckCondolencesTokenMutation()
  const refreshOrdersMutation = useRefreshCondolencesOrdersMutation()
  const [condolencesCredentialsUnlocked, setCondolencesCredentialsUnlocked] = useState(false)
  const status = tokenStatusBadge(integration)
  const isConnected = Boolean(integration.connected)
  const connectionNeedsAttention = Boolean(integration.has_token && !isConnected)
  const unlockCondolencesCredentials = () => setCondolencesCredentialsUnlocked(true)
  const condolencesCredentialInputProps = {
    ...condolencesCredentialInputBaseProps,
    readOnly: !condolencesCredentialsUnlocked,
    onFocus: unlockCondolencesCredentials,
    onPointerDown: unlockCondolencesCredentials,
  }

  const connectForm = useAppForm({
    defaultValues: {
      email: '',
      password: '',
    },
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return undefined

        try {
          await connectLoginMutation.mutateAsync({
            email: value.email.trim(),
            password: value.password,
          })

          toast.success('Condolences connected')
          setTimeout(() => {
            formApi.reset()
          })
        } catch (error) {
          if (readProperty(readProperty(readProperty(error, 'response'), 'data'), 'errors')) {
            return validationErrorsForForm(error, ['email', 'password'])
          }

          toast.error('Could not connect Condolences', {
            description: getErrorMessage(error),
          })
        }
        return undefined
      },
    },
  })

  async function handleDisconnect() {
    try {
      await updateMutation.mutateAsync({
        enabled: false,
        clear_token: true,
      })
      toast.success('Condolences disconnected')
    } catch (error) {
      toast.error('Could not disconnect Condolences', {
        description: getErrorMessage(error),
      })
    }
  }

  async function handleCheckConnection() {
    try {
      await checkTokenMutation.mutateAsync()
      toast.success('Condolences connection is active')
    } catch (error) {
      toast.error('Could not verify Condolences', {
        description: getErrorMessage(error),
      })
    }
  }

  async function handleRefreshOrders() {
    try {
      const response = await refreshOrdersMutation.mutateAsync()
      const toastFn = response?.failed > 0 ? toast.error : toast.success
      toastFn(
        response?.failed > 0
          ? 'Some Condolences orders did not import'
          : 'Condolences orders refreshed',
        {
          description: refreshOrdersDescription(response),
        }
      )
    } catch (error) {
      toast.error('Could not refresh Condolences orders', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="max-w-4xl space-y-5">
      <AdminPageHeader
        icon={CondolencesIcon}
        title="Condolences"
        description="Import memorial orders from Condolences as orders."
        actions={
          isConnected ? (
            <AdminHeaderActionButton
              type="button"
              disabled={isReadOnly || refreshOrdersMutation.isPending}
              onClick={handleRefreshOrders}
            >
              <RefreshCw className="size-4" />
              Refresh orders
            </AdminHeaderActionButton>
          ) : null
        }
      />

      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice>
            Switch to the account that owns the Condolences connection.
          </OrganizationContextNotice>
        }
      >
        <>
          {connectionNeedsAttention && integration.last_error ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Condolences connection needs attention</AlertTitle>
              <AlertDescription>{integration.last_error}</AlertDescription>
            </Alert>
          ) : null}

          {isConnected ? (
            <Card>
              <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-status-success-background text-status-success-foreground ring-1 ring-inset ring-status-success-border dark:bg-status-success-background dark:text-status-success-foreground dark:ring-status-success-border">
                    <CondolencesIcon className="size-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-xl">Condolences account</CardTitle>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </div>
                    <Typography variant="supporting" className="mt-1 block">
                      {integration.vendor_name
                        ? `${integration.vendor_name}${integration.vendor_id ? ` #${integration.vendor_id}` : ''}`
                        : integration.token_preview
                          ? `Token ending ${integration.token_preview.slice(-4)}`
                          : 'Ready to import orders.'}
                    </Typography>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isReadOnly || checkTokenMutation.isPending}
                    onClick={handleCheckConnection}
                  >
                    <ShieldCheck className="size-4" />
                    Verify connection
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="ghostMuted"
                        disabled={isReadOnly || updateMutation.isPending}
                      >
                        <Unplug className="size-4" />
                        Disconnect
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disconnect Condolences?</AlertDialogTitle>
                        <AlertDialogDescription>
                          New Condolences orders will stop importing until you connect again.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDisconnect}>Disconnect</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ) : (
            <connectForm.AppForm>
              <connectForm.Form {...autofillOffFormProps}>
                <Card>
                  <CardHeader>
                    <CardTitle>Connect with Condolences login</CardTitle>
                    <CardDescription>
                      Enter the Condolences account email and password to create a POS API token.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <connectForm.FormError title="Unable to connect Condolences" />

                    <div className="grid gap-4 md:grid-cols-2">
                      <connectForm.AppField
                        name="email"
                        validators={{ onChange: connectCondolencesLoginSchema.shape.email }}
                      >
                        {(field) => (
                          <field.TextInput
                            {...condolencesCredentialInputProps}
                            id="condolences-email-entry"
                            name="condolences_email_entry"
                            type="email"
                            label="Email"
                            placeholder="user@example.com"
                            disabled={isReadOnly}
                          />
                        )}
                      </connectForm.AppField>

                      <connectForm.AppField
                        name="password"
                        validators={{ onChange: connectCondolencesLoginSchema.shape.password }}
                      >
                        {(field) => (
                          <field.TextInput
                            {...condolencesCredentialInputProps}
                            id="condolences-password-entry"
                            name="condolences_password_entry"
                            type="password"
                            label="Password"
                            placeholder="Enter the password"
                            disabled={isReadOnly}
                          />
                        )}
                      </connectForm.AppField>
                    </div>

                    <div className="flex justify-end">
                      <connectForm.SubmitButton
                        mutation={connectLoginMutation}
                        className="w-full md:w-auto"
                        keepChildrenWhileLoading
                      >
                        <KeyRound className="size-4" />
                        Connect Account
                      </connectForm.SubmitButton>
                    </div>
                  </CardContent>
                </Card>
              </connectForm.Form>
            </connectForm.AppForm>
          )}

          {isConnected ? (
            <div className="space-y-5">
              <Card>
                <CardHeader>
                  <CardTitle>Automatic updates</CardTitle>
                  <CardDescription>
                    New and updated Condolences orders import automatically every 5 minutes.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm sm:grid-cols-3">
                  <div className="flex items-start gap-3 rounded-lg border border-border/70 p-3">
                    <RefreshCw className="mt-0.5 size-4 shrink-0 text-interaction" />
                    <span>We check Condolences for new orders and order changes.</span>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border border-border/70 p-3">
                    <Unplug className="mt-0.5 size-4 shrink-0 text-interaction" />
                    <span>Cancelling here cancels in Condolences and refunds the customer.</span>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border border-border/70 p-3">
                    <Truck className="mt-0.5 size-4 shrink-0 text-interaction" />
                    <span>
                      Marking delivered here marks orders delivered in Condolences so you can get
                      paid.
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </>
      </IfGlobalOrg>
    </div>
  )
}
